import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid2 as Grid,
  Icon,
  InputLabel,
  Typography,
} from "@mui/material";
import {
  createBlog,
  retrieveAllBlogCategories,
} from "../../../redux/admin-slices/blogs.slice";
import { toastError } from "../../../redux/guest-slices/other.slice";
import SkeletonImage from "../../../components/common/SkeletonImage";
import AutoCompleteA from "../../../components/ui/AutoCompleteA";
import InputFieldA from "../../../components/ui/InputFieldA";
import SelectInput from "../../../components/ui/SelectInput";
import { useDispatch, useSelector } from "react-redux";
import { ckEditorConfig } from "../../../utils/config";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { useEffect, useRef, useState } from "react";
import { Link as NavLink, useNavigate } from "react-router-dom";
import Icons from "../../../assets/js/Icons";
import { ClassicEditor } from "ckeditor5";
import "ckeditor5/ckeditor5.css";

function AddBlog() {
  const files = useRef("");
  const editorRef = useRef("");
  const categoryRef = useRef("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [data, setData] = useState({});
  const [image, setImage] = useState(null);
  const [tags, setTags] = useState([]);
  const { loadings } = useSelector((state) => state.otherSlice);

  const handleGetAll = () => {
    dispatch(
      retrieveAllBlogCategories({
        limit: 100,
      })
    )
      .unwrap()
      .then((data) => {
        setData(data);
      })
      .catch(() => { });
  };

  const handleImagePreview = (e) => {
    if (e.target.files.length) {
      files.current = Array.from(e.target.files);
      const views = files.current?.map((img) => URL.createObjectURL(img));
      setImage(views);
    }
  };

  // Handle create blog
  const handleSubmit = (e) => {
    e.preventDefault();
    if (loadings?.create) return;
    const form = e.target;
    const formData = Object.fromEntries(new FormData(form));
    formData.banner = files?.current || null;
    formData.tags = tags;
    formData.description = editorRef?.current?.data || null;
    formData.categoryId = categoryRef?.current?.value?._id || null;

    if (!formData.categoryId) {
      return dispatch(toastError("Category is required!"));
    }
    if (!formData.title || formData.title.trim() === "") {
      return dispatch(toastError("Title is required!"));
    }
    if (!formData.description || formData.description.trim() === "") {
      return dispatch(toastError("Description is required!"));
    }
    if (!formData.banner || formData.banner.length === 0) {
      return dispatch(toastError("Banner image is required!"));
    }
    if (!formData.source || formData.source.trim() === "") {
      return dispatch(toastError("Source is required!"));
    }
    if (!formData.tags || formData.tags.length === 0) {
      return dispatch(toastError("Tags are required!"));
    }

    dispatch(createBlog(formData))
      .unwrap()
      .then(() => {
        form.reset();
        navigate("/blogs/all");
      })
      .catch(() => { });
  };

  useEffect(() => {
    handleGetAll();

    // eslint-disable-next-line
  }, []);

  return (
    <Container maxWidth={false} fixed sx={{ py: 5 }}>
      <Box className="overview-container">
        <Typography variant="pageTitle">Add new blog post</Typography>
        <Button
          variant="outlined"
          to="/blogs/all"
          LinkComponent={NavLink}
          startIcon={<Icons.ListSolid />}
        >
          View All Blogs
        </Button>
      </Box>

      <Box className="page-content">
        <Grid container spacing={3} component="form" onSubmit={handleSubmit}>
          <Grid size={{ xs: 12, xl: 6 }}>
            <SelectInput
              label="Category *"
              renderValueKey="name"
              inputRef={categoryRef}
              placeholder="Enter Question"
              selectLabel="Select Category"
              loading={loadings?.retrieveAll}
              options={data?.records || []}
            />
          </Grid>
          <Grid size={{ xs: 12, xl: 6 }}>
            <InputFieldA
              name="title"
              label="Title *"
              placeholder="Enter Title"
            />
          </Grid>
          <Grid size={12} sx={{ ".ck-content": { minHeight: "200px" } }}>
            <CKEditor
              ref={editorRef}
              onChange={(_, editor) => {
                editorRef.current.data = editor.getData();
              }}
              editor={ClassicEditor}
              config={ckEditorConfig}
            />
          </Grid>
          <Grid size={12}>
            <InputLabel
              sx={{
                mb: 0,
                height: "100%",
                maxHeight: 300,
                borderRadius: "8px",
                border: "1px dashed",
                placeContent: "center",
                borderColor: "primary.main",
                display: image ? "block" : "grid",
              }}
            >
              {image ? (
                <SkeletonImage
                  sx={{ objectFit: "contain" }}
                  src={image || null}
                />
              ) : (
                <>
                  <Icon
                    sx={{
                      fontSize: "100px",
                      color: "other.color2",
                      m: "auto",
                      svg: { display: "block" },
                    }}
                  >
                    <Icons.ImageSolid />
                  </Icon>
                  <Typography mb={1.5} color="other.color2">
                    Upload banner image *
                  </Typography>
                </>
              )}
              <input
                style={{ display: "none" }}
                onChange={handleImagePreview}
                accept="image/*"
                type="file"
              />
              {/*  */}
            </InputLabel>
          </Grid>
          <Grid size={{ xs: 12, xl: 6 }}>
            <InputFieldA
              name="source"
              label="Source *"
              placeholder="Enter source"
            />
          </Grid>
          <Grid size={{ xs: 12, xl: 6 }}>
            <AutoCompleteA
              multiple
              freeSolo
              name="tags"
              value={tags}
              label="Tags *"
              placeholder="Enter tags"
              onChange={(_, value) => {
                setTags(value);
              }}
            />
          </Grid>
          <Grid size={12}>
            <Button
              type="submit"
              variant="contained"
              sx={{ minWidth: "150px" }}
              disabled={loadings?.create}
              endIcon={
                loadings?.create && (
                  <CircularProgress size={22} color="text" thickness={6} />
                )
              }
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}

export default AddBlog;
